import { create } from '@bufbuild/protobuf'
import { anyPack } from '@bufbuild/protobuf/wkt'
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Checkbox,
    CheckboxProps,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    MenuItem,
    Tab,
    Tabs,
    TextField,
} from '@mui/material'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { z } from 'zod/v4-mini'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { useSetUserSettings } from '@/features/layout/mutations/useSetUserSetting'
import { useUpdateUserPassword } from '@/features/settings/mutations/useUpdateUserPassword'
import { usePasswordSchema } from '@/features/settings/schemas/passwordSchema'
import { UserConfigurationEntryReadPermission } from '@/generated/Enumerations/UserConfigurationEntryReadPermission_pb'
import {
    UserConfigurationEntryDto_BooleanSettingSchema,
    UserConfigurationEntryDtoSchema,
} from '@/generated/Messages/Basic_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { parseId } from '@/helper/id'
import {
    timeProgressToDateKey,
    useCalendarForTrackingsOverviewKey,
} from '@/helper/userSettingsKeys'
import { useAppForm } from '@/hooks/useAppForm'
import { setLanguageLocalStorage } from '@/i18n'
import { useBooleanUserSetting } from '@/queries/useUserSettings'

const seleniumPrefix = 'settings'

const RouteComponent: React.FC = () => {
    const { user } = useAuth()

    const [t, i18n] = useTranslation()

    const navigate = Route.useNavigate()

    const { tab } = Route.useSearch()

    const passwordSchema = usePasswordSchema()
    const updateUserPassword = useUpdateUserPassword()
    const { addFeedback } = useFeedbackContext()
    const passwordForm = useAppForm({
        defaultValues: {
            currentPassword: '',
            password: '',
            passwordRepeat: '',
        },
        validators: { onSubmit: passwordSchema },

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await updateUserPassword.mutateAsync(value)

            formApi.reset()
        },
    })

    const { data: timeProgressToDate } = useBooleanUserSetting(
        timeProgressToDateKey
    )
    const setTimeProgressToDate = useSetUserSettings()
    const { data: useCalendarForTrackings } = useBooleanUserSetting(
        useCalendarForTrackingsOverviewKey
    )
    const setUseCalendarForTrackings = useSetUserSettings()

    const onChangeTimeProgressToDate =
        (
            field: 'timeProgressToDate' | 'useCalendarForTrackings'
        ): NonNullable<CheckboxProps['onChange']> =>
        async (_, value) => {
            assertNotNullish(user)

            const config = create(UserConfigurationEntryDtoSchema, {
                identifier: {
                    configurationKey:
                        field === 'timeProgressToDate' ?
                            timeProgressToDateKey
                        :   useCalendarForTrackingsOverviewKey,
                    userId:
                        user.profile.sub ?
                            parseId(user.profile.sub)
                        :   undefined,
                },

                tenantId: user.profile.tenant,

                readPermission:
                    UserConfigurationEntryReadPermission.PRIVATE_SELF_ONLY,

                configurationValue: anyPack(
                    UserConfigurationEntryDto_BooleanSettingSchema,
                    create(UserConfigurationEntryDto_BooleanSettingSchema, {
                        value,
                    })
                ),
            })

            if (field === 'timeProgressToDate') {
                await setTimeProgressToDate.mutateAsync(config)
            } else {
                await setUseCalendarForTrackings.mutateAsync(config)
            }
        }

    return (
        <Card>
            <title>{t('settings.pageTitle')}</title>

            <CardHeader title={t('settings.pageTitle')} />
            <Tabs
                sx={{ marginLeft: 2 }}
                value={tab}
                onChange={(_, value: TabSchema) =>
                    navigate({ search: prev => ({ ...prev, tab: value }) })
                }
            >
                <Tab
                    label={t('settings.commonTab')}
                    value={'Common_pb'}
                    data-selenium={`${seleniumPrefix}-common-tab`}
                />
                <Tab
                    label={t('settings.passwordTab')}
                    value={'password'}
                    data-selenium={`${seleniumPrefix}-password-tab`}
                />
            </Tabs>

            {tab === 'Common_pb' ?
                <CardContent>
                    <TextField
                        label={t('settings.language')}
                        value={i18n.language}
                        onChange={async ({ target: { value } }) => {
                            await i18n.changeLanguage(value)

                            setLanguageLocalStorage(value)
                        }}
                        select={true}
                        margin={'normal'}
                        data-selenium={`${seleniumPrefix}-language`}
                    >
                        <MenuItem value={'de'}>
                            {t('settings.languages.german')}
                        </MenuItem>
                        <MenuItem value={'en'}>
                            {t('settings.languages.english')}
                        </MenuItem>
                    </TextField>

                    <FormControl
                        data-selenium={`${seleniumPrefix}-time-progress-to-date`}
                        fullWidth={true}
                        margin={'normal'}
                    >
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={timeProgressToDate}
                                        onChange={onChangeTimeProgressToDate(
                                            'timeProgressToDate'
                                        )}
                                    />
                                }
                                label={t('settings.timeProgressToDate')}
                                disabled={setTimeProgressToDate.isPending}
                            />
                        </FormGroup>
                        <FormHelperText sx={{ marginLeft: 0 }}>
                            {t('settings.timeProgressToDateHelper')}
                        </FormHelperText>
                    </FormControl>

                    <FormControl
                        data-selenium={`${seleniumPrefix}-use-calendar-for-trackings-overview`}
                        fullWidth={true}
                        margin={'normal'}
                    >
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useCalendarForTrackings}
                                        onChange={onChangeTimeProgressToDate(
                                            'useCalendarForTrackings'
                                        )}
                                    />
                                }
                                label={t('settings.useCalendarForTrackings')}
                                disabled={setUseCalendarForTrackings.isPending}
                            />
                        </FormGroup>
                        <FormHelperText sx={{ marginLeft: 0 }}>
                            {t('settings.useCalendarForTrackingsHelper')}
                        </FormHelperText>
                    </FormControl>
                </CardContent>
            :   <passwordForm.AppForm>
                    <form
                        onSubmit={async e => {
                            e.preventDefault()
                            e.stopPropagation()

                            await passwordForm.handleSubmit()
                        }}
                    >
                        <CardContent>
                            <passwordForm.AppField name={'currentPassword'}>
                                {field => (
                                    <field.TextField
                                        label={t('user.currentPassword')}
                                        margin={'normal'}
                                        type={'password'}
                                        data-selenium={`${seleniumPrefix}-current-password`}
                                    />
                                )}
                            </passwordForm.AppField>
                            <passwordForm.AppField name={'password'}>
                                {field => (
                                    <field.TextField
                                        label={t('user.password')}
                                        margin={'normal'}
                                        type={'password'}
                                        data-selenium={`${seleniumPrefix}-password`}
                                    />
                                )}
                            </passwordForm.AppField>
                            <passwordForm.AppField name={'passwordRepeat'}>
                                {field => (
                                    <field.TextField
                                        label={t('user.passwordRepeat')}
                                        margin={'normal'}
                                        type={'password'}
                                        data-selenium={`${seleniumPrefix}-password-repeat`}
                                    />
                                )}
                            </passwordForm.AppField>
                        </CardContent>

                        <CardActions sx={{ justifyContent: 'flex-end' }}>
                            <passwordForm.Subscribe
                                selector={state =>
                                    state.isSubmitting || !state.isFormValid
                                }
                            >
                                {disabled => (
                                    <Button
                                        disabled={disabled}
                                        type={'submit'}
                                        data-selenium={`${seleniumPrefix}-save`}
                                    >
                                        {t('common.save')}
                                    </Button>
                                )}
                            </passwordForm.Subscribe>
                        </CardActions>
                    </form>
                </passwordForm.AppForm>
            }
        </Card>
    )
}

const tabSchema = z.enum(['Common_pb', 'password'])
type TabSchema = z.infer<typeof tabSchema>

export const Route = createFileRoute('/__base-layout/settings')({
    component: RouteComponent,

    validateSearch: z.object({ tab: z._default(tabSchema, 'Common_pb') }),

    search: { middlewares: [stripSearchParams({ tab: 'Common_pb' })] },
})
